// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { z } from "zod";
// ---------------------------------------------------------------------------
// Amy truth contract (ADR-048, 2026-09-02). Backend-owned product facts Amy
// (and any client) may state about how BiteBalance currently behaves: which
// capabilities exist, which stored settings are behaviorally ACTIVE, and
// what the latest plan job authoritatively concluded. Amy may explain,
// recommend, or act on product behavior ONLY from this state and tool
// results — never from stale fields, deprecated fields, or prompt-era
// assumptions. The planning service is the single owner of these facts;
// nothing here is derived client-side or in the prompt.
// ---------------------------------------------------------------------------
/** One stored user setting and whether it currently changes product behavior. */
export const SettingActivitySchema = z.object({
    setting: z.string(),
    /** The stored value, verbatim (null when unset — and ALWAYS null for
     * inactive settings: an inactive setting's number is not actionable
     * information and must never feed a recommendation). */
    storedValue: z.union([z.string(), z.number(), z.boolean()]).nullable(),
    /** TRUE only when changing this setting changes planning/product behavior TODAY. */
    active: z.boolean(),
    /** Short human/agent-readable reason, mandatory when inactive. */
    note: z.string().nullable(),
});
export const ProductCapabilitiesSchema = z.object({
    /** Ingredient reuse / waste minimization is part of EVERY generation —
     * never an optional mode a user must enable. */
    baselineIngredientReuse: z.literal("always_active"),
    /** Rebuild composition priorities a user may explicitly request. These
     * change plan COMPOSITION for one rebuild; none is a cheaper-plan
     * guarantee — budget feasibility is decided by the cost search below. */
    optimizationPriorities: z.array(z.enum(["balanced", "low_waste", "high_variety"])),
    /** Generation runs its full deterministic cost-pressure search before
     * declaring BUDGET_INFEASIBLE — there is no cheaper retry with the same
     * inputs. */
    exhaustiveCostSearch: z.literal(true),
    /** Per-meal portion scaling exists via the preview→approve proposal flow. */
    perMealPortionScaling: z.literal(true),
    /** Household/multi-person planning is not active in the pilot. */
    householdPlanning: z.literal(false),
    /** Weekly cost totals (cheapest-valid, budget verdicts) are THIS WEEK'S
     * CHECKOUT — they can include stock-up purchases whose value carries
     * into later weeks; they are NOT proven recurring weekly spend. */
    weeklyCostSemantics: z.literal("first_shop_checkout"),
    /** Budget Model v2 (ADR-052): BiteBalance does NOT record what users
     * actually pay — no receipts, no purchase prices, no trip totals. The
     * "actual weekly average" is therefore NOT TRACKED YET and is never
     * derived from estimates. Literal false until a separate capture
     * capability ships (its own SPEC). */
    actualSpendingTracking: z.literal(false),
});
export const LatestPlanJobStateSchema = z.object({
    status: z.enum(["queued", "running", "completed", "failed"]),
    optimizationPriority: z.enum(["balanced", "low_waste", "high_variety"]).nullable(),
    error: z
        .object({ code: z.string(), details: z.record(z.string(), z.unknown()).optional() })
        .nullable(),
    /** FALSE means the same request would deterministically fail again —
     * only changed inputs (budget, constraints, preferences) can change the
     * outcome. */
    retryableWithSameInputs: z.boolean(),
    /** TRUE when the planner already exhausted its lower-cost attempts for
     * this outcome (always true for BUDGET_INFEASIBLE — the cheapest valid
     * plan in error.details IS the floor the search found). */
    costAttemptsExhausted: z.boolean(),
});
/** The ADR-050 money split for a budget result, in cents. */
export const BudgetEconomicsBreakdownSchema = z.object({
    /** Real cash at the till for the cheapest valid first shop. */
    checkoutCashCents: z.number(),
    /** The part of that checkout eaten this week. */
    consumedThisWeekValueCents: z.number(),
    /** The part that stays in the pantry for future meals — explains the
     * higher first shop; it does not reduce the checkout. */
    pantryCarryoverValueCents: z.number(),
    /** Estimated steady-state weekly food cost once the pantry is stocked —
     * the number a weekly budget goal compares against. Null when the
     * estimate could not be computed. */
    estimatedWeeklyFoodValueCents: z.number().nullable(),
    /** Owner invariant 2026-09-04: false when the split covers priced rows
     * only (a required row is unpriced) — clients hide it then; absent on
     * pre-completeness revisions (treat as unknown → hide). */
    complete: z.boolean().optional(),
});
/**
 * S-7 (ADR-050 amendment, 2026-09-03): the authoritative budget-economics
 * truth state, served on EVERY product-state read so Amy never has to
 * infer economics — or their availability — from history or tool choice.
 * Either the actual values are here, or `economicsAvailable` is false
 * with an explicit reason. A result predating the ADR-050 breakdown can
 * NOT be recomputed (the failed attempt's candidate shopping rows are
 * never persisted) and is never fabricated.
 */
/** Budget Model v2: the checkout / goal verdicts persisted with the CURRENT
 * plan revision (the same ONE cost model every surface reads). */
export const BudgetCurrentVerdictSchema = z.object({
    revisionNumber: z.number(),
    checkout: z.object({
        costCents: z.number(),
        maxCents: z.number().nullable(),
        maxSource: z.enum(["maximum_checkout", "legacy_limit"]).nullable(),
        status: z.enum(["no_max", "within_max", "exceeds_max", "unverifiable"]),
        deltaCents: z.number().nullable(),
    }),
    goal: z.object({
        forecastWeeklyAverageCents: z.number().nullable(),
        goalCents: z.number().nullable(),
        /** EXACT: at_or_below_goal iff forecast ≤ goal; above_goal iff forecast > goal. No tolerance band (owner D-3). */
        status: z.enum(["at_or_below_goal", "above_goal", "unavailable"]),
        /** forecast − goal, signed, exact. */
        deltaCents: z.number().nullable(),
        unavailableReason: z.enum(["no_goal", "legacy_unclassified", "unpriced_rows", "no_economics"]).nullable(),
    }),
    economics: BudgetEconomicsBreakdownSchema.nullable(),
});
export const ForecastWeeklyAverageSchema = z.object({
    cents: z.number().nullable(),
    available: z.boolean(),
    /** How the forecast is produced. v1: the ADR-050 steady-state estimate of
     * the CURRENT plan — an estimate from THIS week's plan, NOT a simulation
     * of future weeks. New engines add new basis ids; the user's mental model
     * ("our estimate of your average weekly grocery spend") never changes. */
    basis: z.literal("steady_state_estimate_current_plan"),
    unavailableReason: z.enum(["no_current_plan", "unpriced_rows", "no_economics"]).nullable(),
});
/** Owner decision D-4 (2026-09-04): "Actual weekly average — Not tracked
 * yet" until genuine spend capture exists. `cents` is ALWAYS null and
 * `available` ALWAYS false in Budget Model v2; nothing may derive a number
 * from plan estimates, check-marks or savings events. */
export const ActualWeeklyAverageSchema = z.object({
    cents: z.null(),
    available: z.literal(false),
    unavailableReason: z.literal("no_recorded_spending"),
    requires: z.literal("recorded_shopping_trips"),
});
export const BudgetEconomicsStateSchema = z.object({
    /** Budget Model v2 (ADR-052): which model the account is on. A
     * `legacy_hard_cap` account carries an UNCLASSIFIED pre-v2 amount in
     * `legacyWeeklyLimitCents` — enforced as a hard ceiling for backward
     * compatibility, NEVER represented as a goal or a chosen maximum (both v2
     * fields are null until the user classifies it in Settings). */
    budgetModel: z.enum(["v2", "legacy_hard_cap", "none"]),
    /** SOFT weekly spending goal (average over time; single shops may differ). */
    weeklyGoalCents: z.number().nullable(),
    /** HARD optional maximum per checkout (no override). */
    maxCheckoutCents: z.number().nullable(),
    /** The unclassified pre-v2 amount (legacy accounts only). */
    legacyWeeklyLimitCents: z.number().nullable(),
    /** @deprecated pre-v2 name: the HARD ceiling in force (maximum checkout,
     * else the legacy limit; null when none). Never the goal. */
    weeklyBudgetCents: z.number().nullable(),
    /** The current plan revision's persisted verdicts (null when no plan or
     * a pre-v2 revision without them). */
    current: BudgetCurrentVerdictSchema.nullable(),
    forecastWeeklyAverage: ForecastWeeklyAverageSchema,
    actualWeeklyAverage: ActualWeeklyAverageSchema,
    /** The latest BUDGET_INFEASIBLE outcome, or null when the latest job is
     * not a budget failure (running/completed/other failure/no job). */
    latestFailedAttempt: z
        .object({
        /** Budget Model v2: ONLY the hard ceiling can fail a rebuild. */
        constraint: z.literal("max_checkout"),
        maxSource: z.enum(["maximum_checkout", "legacy_limit"]).nullable(),
        /** Owner invariant 2026-09-04: whether this failure still APPLIES to
         * the user's current settings (`current`) or is history only
         * (`superseded_by_settings` — ceiling removed/changed or any profile
         * change since). A superseded attempt is never narrated as the user's
         * current maximum. Same derivation as the plan response's
         * `latestJob.applicability`. */
        applicability: z.object({
            status: z.enum(["current", "superseded_by_settings"]),
            reason: z.enum(["ceiling_removed", "ceiling_changed", "settings_changed"]).nullable(),
            constraintSource: z.enum(["maximum_checkout", "legacy_limit"]).nullable(),
            attemptCeilingCents: z.number().nullable(),
            currentCeilingCents: z.number().nullable(),
            currentCeilingSource: z.enum(["maximum_checkout", "legacy_limit"]).nullable(),
        }),
        cheapestValidCostCents: z.number(),
        gapCents: z.number(),
        /** The budget the attempt actually ran with. */
        attemptBudgetCents: z.number().nullable(),
        /** FALSE when the budget target or profile changed after this
         * attempt — its numbers describe superseded settings and must not
         * be reused as current economics. */
        matchesCurrentSettings: z.boolean(),
        economicsAvailable: z.boolean(),
        unavailableReason: z
            .enum(["predates_economics_breakdown", "settings_changed_since_result"])
            .nullable(),
        /** Present ONLY when economicsAvailable — never reconstructed. */
        economics: BudgetEconomicsBreakdownSchema.nullable(),
    })
        .nullable(),
});
/**
 * ADR-057 — TEMPORAL TRUTH: the product's authoritative local clock
 * (Europe/Berlin, the same rule the day ledger uses for "today"). Served on
 * product state so web, mobile and Amy share one clock; today / tonight /
 * tomorrow and meal timing derive from it. `dayPart` bands (local time):
 * night 00:00–05:59, morning 06:00–10:59, midday 11:00–13:59, afternoon
 * 14:00–17:59, evening 18:00–21:59, late_evening 22:00–23:59.
 */
export const ClockSchema = z.object({
    /** The instant, UTC ISO-8601. */
    nowIso: z.string(),
    timeZone: z.literal("Europe/Berlin"),
    /** Local calendar date — the product's "today". */
    localDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Local wall-clock time, HH:MM (24 h). */
    localTime: z.string().regex(/^\d{2}:\d{2}$/),
    /** Local date-time with the UTC offset, ISO-8601 (e.g. 2026-09-05T23:30:00+02:00). */
    localDateTime: z.string(),
    utcOffset: z.string().regex(/^[+-]\d{2}:\d{2}$/),
    weekday: z.enum(["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]),
    dayPart: z.enum(["night", "morning", "midday", "afternoon", "evening", "late_evening"]),
});
export const ProductStateResponseSchema = z.object({
    capabilities: ProductCapabilitiesSchema,
    settingsActivity: z.array(SettingActivitySchema),
    latestPlanJob: LatestPlanJobStateSchema.nullable(),
    /** ADR-057: the authoritative local clock (optional so stored/older responses still parse). */
    clock: ClockSchema.optional(),
    budgetEconomics: BudgetEconomicsStateSchema,
});
//# sourceMappingURL=productState.js.map